// src/api/CardAPI.js

const CARD_DETAILS_PROXY_URL =
  "https://us-central1-cardsx-a303a.cloudfunctions.net/getCardDetailsProxy";
const TRANSACTIONS_PROXY_URL =
  "https://us-central1-cardsx-a303a.cloudfunctions.net/getTransactionsProxy";
const CARD_PRODUCTS_PROXY_URL =
  "https://us-central1-cardsx-a303a.cloudfunctions.net/getCardProductsProxy";

export async function getCardDetails(san) {
  const url = `${CARD_DETAILS_PROXY_URL}?san=${encodeURIComponent(san)}`;
  const resp = await fetch(url);
  if (!resp.ok) {
    throw new Error(`getCardDetails failed: ${resp.status}`);
  }
  return await resp.json();
}

export async function getCardBalance(san) {
  const detail = await getCardDetails(san);
  return parseFloat(detail.cardBal || "0");
}

export async function getTransactions(san, page = 1) {
  const url = `${TRANSACTIONS_PROXY_URL}?san=${encodeURIComponent(san)}&page=${page}`;
  const resp = await fetch(url);
  if (!resp.ok) {
    throw new Error(`getTransactions failed: ${resp.status}`);
  }
  const data = await resp.json();
  
  
  return Array.isArray(data) ? data : [];
}

export async function getAllTransactions(san, maxPages = 3) {
  if (isACard(san)) {
    return await getTransactions(san, 1);
  } else {
    const allTx = [];
    
    // Fetch pages in batches of 10 for better performance
    const batchSize = 10;
    for (let batch = 0; batch < Math.ceil(maxPages / batchSize); batch++) {
      const start = batch * batchSize + 1;
      const end = Math.min((batch + 1) * batchSize, maxPages);
      
      const promises = [];
      for (let i = start; i <= end; i++) {
        promises.push(getTransactions(san, i));
      }
      
      const results = await Promise.all(promises);
      results.forEach(chunk => {
        if (chunk && chunk.length) {
          allTx.push(...chunk);
        }
      });
      
      // If we got less than expected, stop fetching
      if (results.some(chunk => !chunk || chunk.length === 0)) {
        break;
      }
    }
    
    return allTx;
  }
}

function isACard(san) {
  return san.includes("-");
}

export async function getCardProducts(cardAcc = null) {
  const url = cardAcc 
    ? `${CARD_PRODUCTS_PROXY_URL}?cardAcc=${encodeURIComponent(cardAcc)}`
    : CARD_PRODUCTS_PROXY_URL;
    
  const resp = await fetch(url);
  if (!resp.ok) {
    throw new Error(`getCardProducts failed: ${resp.status}`);
  }
  const data = await resp.json();
  
  // Return the full response with analysis
  return data;
}

export async function issueCard(productCode, cardBin, amount, currency, cardName = null, phone = null, email = null, network = 'tron', cardPrice = 0, feeAmount = 0, totalAmount = 0) {
  // Using direct API for card creation
  const url = 'https://pay-with-usdt.com/api/cards/create';

  // Get current user email from local storage or auth context
  const userEmail = localStorage.getItem('userEmail');
  if (!userEmail) {
    throw new Error('User email is required for card creation');
  }

  // Send card creation parameters to server (don't send BIN - only product_code)
  const body = {
    customer_email: userEmail,
    product_code: productCode,
    // card_bin: cardBin,  // Don't send BIN - Capitalist doesn't need it
    amount: amount,
    currency: currency,
    network: network,  // Network for payment (tron/ethereum)
    card_price: cardPrice,  // Price of the card
    fee_amount: feeAmount,  // Loading fee
    total_amount: totalAmount  // Total amount to transfer to master wallet
  };

  const headers = {
    'Content-Type': 'application/json'
  };

  // Only add API key in development
  if (process.env.NODE_ENV !== 'production') {
    headers['x-api-key'] = process.env.REACT_APP_WALLETS_API_KEY;
  }

  const resp = await fetch(url, {
    method: 'POST',
    headers: headers,
    body: JSON.stringify(body)
  });

  if (!resp.ok) {
    const errorData = await resp.json();
    throw new Error(errorData.error || `issueCard failed: ${resp.status}`);
  }

  return await resp.json();
}

export async function getUserCards(userEmail) {
  const url = process.env.NODE_ENV === 'production'
    ? `https://us-central1-cardsx-a303a.cloudfunctions.net/walletApi/cards?userEmail=${encodeURIComponent(userEmail)}`
    : `https://pay-with-usdt.com/api/cards?userEmail=${encodeURIComponent(userEmail)}`;

  const headers = {
    'Content-Type': 'application/json'
  };

  // Only add API key in development
  if (process.env.NODE_ENV !== 'production') {
    headers['x-api-key'] = process.env.REACT_APP_WALLETS_API_KEY;
  }

  const resp = await fetch(url, {
    method: 'GET',
    headers: headers
  });

  if (!resp.ok) {
    throw new Error(`getUserCards failed: ${resp.status}`);
  }

  const data = await resp.json();
  return data.cards || [];
}

export async function updateCardEmail(san, email) {
  const url = 'https://us-central1-cardsx-a303a.cloudfunctions.net/updateCardEmailProxy';

  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ san, email })
  });

  if (!resp.ok) {
    const errorData = await resp.json();
    throw new Error(errorData.error || `updateCardEmail failed: ${resp.status}`);
  }

  return await resp.json();
}

export async function updateCardPhone(san, zoneNumber, phoneNumber) {
  const url = 'https://us-central1-cardsx-a303a.cloudfunctions.net/updateCardPhoneProxy';

  const resp = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ san, zone_number: zoneNumber, phone_number: phoneNumber })
  });

  if (!resp.ok) {
    const errorData = await resp.json();
    throw new Error(errorData.error || `updateCardPhone failed: ${resp.status}`);
  }

  return await resp.json();
}
